/**
 * Created by fers on 05.02.16.
 */

import Link from 'react-router/lib/Link';
import {injectIntl, FormattedMessage, IntlShape} from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import {Tooltip, OverlayTrigger} from 'react-bootstrap';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import * as Tool from './Tool';
import {maxTextLength} from '../fw/Tool';
import EmptyContent from '../view/box/system/EmptyContent.react';
import {processPagingParams as processPagingParams_} from './PagingGrid.react';
import LoadingButton from '../fw/LoadingButton.react';
import { PageInfo } from '../../common/types/types';

export const processPagingParams = processPagingParams_;

export function compoundApplicationName(channelDefName, channelName) {
	let name = channelDefName;

	if (channelName) {
		name += ' - ' + channelName;
	}

	return name;
}

interface GridListProps {
    asRow?: boolean;
    bothTitles?: boolean;
    defaultIcon?: string;
    edges: object;
    getUrl?: (...args: unknown[]) => unknown;
    iconProperty: string;
    intl?: object;
    invalid?: boolean;
    itemClassName?: string;
    metadata: object;
    onClick?: (...args: unknown[]) => unknown;
    pageInfo?: PageInfo;
    relay: object;
    renderLink?: boolean;
    sortByTitle?: boolean;
    tagsProperty?: string;
    titleProperty: string;
    titleProperty2?: string;
    tooltip?: (...args: unknown[]) => unknown;
    urlMapping?: object;
}

class GridList extends React.Component<GridListProps> {

    static propTypes = {
		asRow: PropTypes.bool,
		bothTitles: PropTypes.bool,
        defaultIcon: PropTypes.string,
        edges: PropTypes.object.isRequired,
        getUrl: PropTypes.func,
        iconProperty: PropTypes.string.isRequired,
		intl: PropTypes.object,
		invalid: PropTypes.bool,
		itemClassName: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.func,
		]),
        metadata: PropTypes.object.isRequired,
        onClick: PropTypes.func,
        pageInfo: PropTypes.object,
        relay: PropTypes.object.isRequired,
        renderLink: PropTypes.bool,
		sortByTitle: PropTypes.bool,
        tagsProperty: PropTypes.string,
        titleProperty: PropTypes.string.isRequired,
        titleProperty2: PropTypes.string,
		tooltip: PropTypes.func,
        urlMapping: PropTypes.object
    };

    static defaultProps = {
        renderLink: true,
		asRow: true,
    };

	nextPage = () => {
        return new Promise((resolve, reject) => {
            let currentUrl = window.location.href;
            window.history.pushState("", "", currentUrl + '?after=' + encodeURIComponent(this.props.pageInfo.endCursor));

            const cursor = this.props.pageInfo.endCursor;
            this.props.relay.setVariables({ after: cursor }, (state) => {
                if (state.done) {
                    resolve();
                } else if (state.error) {
                    reject();
                }
            });
        });
    };

	getTitle(item) {
		const {titleProperty, titleProperty2, metadata} = this.props;

		let title1 = Tool.getDataForColumn(titleProperty, metadata.fields(), item);

		let title2;
		if (titleProperty2) {
			title2 = Tool.getDataForColumn(titleProperty2, metadata.fields(), item);
		}

		if (this.props.bothTitles && title1 && title2) {
			return compoundApplicationName(title2, title1);
		}

		if (title2) {
			return title2;
		}

		return title1;
	}

	getItemClass(item) {
		let itemClassName = '';

		if (typeof this.props.itemClassName === 'string') {
			itemClassName = this.props.itemClassName;
		}
		else if (typeof this.props.itemClassName === 'function') {
			itemClassName = this.props.itemClassName(item);
		}

		return itemClassName;
	}

	getUrl(item) {
		const {urlMapping, metadata} = this.props;
		let url;

		if (this.props.getUrl) {
			url = this.props.getUrl(item);
		} else if (urlMapping) {
			url = urlMapping.getUrl(metadata, 'view', item);
		}

		return url;
	}

    render() {
        let self = this;
        const {metadata} = this.props;

        let list = this.props.edges.edges
						.map(({node}) => node)
						.map((item) => {
							item.gridListTitle = this.getTitle(item) || '?';
							return item;
						});

		if (this.props.sortByTitle) {
			list = list.sort((itemA, itemB) => {
				return itemA.gridListTitle.toLowerCase().localeCompare(itemB.gridListTitle.toLowerCase());
			});
		}

        let primaryKey = metadata.getPrimaryKey().key;

        if (list.length === 0) {
            return <EmptyContent></EmptyContent>;
        }

        let currentUrl = "";
        if (typeof window === 'object' && 'location' in window) {
            currentUrl = window.location.pathname;
        }

        return (
            <div>
                <div className={"gridList" + (this.props.asRow ? " row" : '')}>
                    {list.map(item => {
                        let tagsTag = null;
                        if (self.props.tagsProperty) {
                            let columnMappings = Tool.getMappingForColumnAsArray(self.props.tagsProperty, metadata.fields());
                            let type = columnMappings[columnMappings.length - 1];
                            let parentType = metadata;
                            if (columnMappings.length > 1) {
                                parentType = columnMappings[columnMappings.length - 2].to();
                            }
                            let tags = Tool.getDataForColumn(self.props.tagsProperty, metadata.fields(), item);
                            if (tags) {
                                if (!('hide' in type['values'][tags]) && !type['values'][tags].hide) {
                                    let style = {};
                                    if (type['values'][tags].color) {
                                        style['backgroundColor'] = type['values'][tags].color;
                                    }
									let badge = Tool.tranformDataForView(parentType.entity(), type, this.props.intl.formatMessage, tags, true);
                                    tagsTag = (
                                        <span aria-hidden className={"badge gridList-badge " + badge.toLowerCase()} style={style}>{badge}</span>);
                                }
                            }
                        }

                        let content = (
                            <div className={item.invalid ? "invalidChannels" : ""}>
                                <div className="gridList-icon">
									{(() => {
										// If iconProperty points to e.g. "channelDef.icon", try to pass the whole object
										// so ChannelIcon can render extendedFrom badge consistently.
										let channelDefObj = null;
										if (self.props.iconProperty && self.props.iconProperty.endsWith('.icon')) {
											const parentPath = self.props.iconProperty.slice(0, -('.icon'.length));
											channelDefObj = Tool.getDataForColumn(parentPath, metadata.fields(), item);
										}

										const icon = Tool.getDataForColumn(self.props.iconProperty, metadata.fields(), item) || this.props.defaultIcon;

										const channelDefForIcon =
											channelDefObj && typeof channelDefObj === 'object'
												? channelDefObj
												: {icon};

										return (<ChannelIcon channelDef={channelDefForIcon} size={64}/>);
									})()}

									{tagsTag}
								</div>
                                <div className="gridList-title">{maxTextLength(item.gridListTitle)}</div>
                            </div>
                        );

						if (this.props.tooltip && this.props.tooltip(item) !== '') {
							const tooltip = (
								<Tooltip id="tooltip">
									{this.props.tooltip(item)}
								</Tooltip>
							);

							content = (
								<OverlayTrigger delay={200} overlay={tooltip} placement="bottom">
									{content}
								</OverlayTrigger>
							);
						}

                        let url = this.getUrl(item);

                        return (
                            <div
                                className={"gridItem " + this.getItemClass(item)}
                                key={item[primaryKey] + "_gridlist"}
                            >
                                <div className="gridList-box" data-dativery-app-type={item.type ? item.type : (item.channelDef && item.channelDef.type ? item.channelDef.type : null)}>
                                    {this.props.onClick
										? <div className="gridList-pointable" onClick={(event) => { this.props.onClick(item, event); }}>{content}</div>
                                        : (this.props.renderLink
											? <Link href={url} to={url}>
													{content}
												</Link>
                                            : <a href={url} target="_blank">
													{content}
												</a>
                                        )
                                    }{" "}
                                </div>
                            </div>
                        );
                    })}
					<div className="clear"></div>
                </div>

				{this.props.pageInfo && this.props.pageInfo.hasNextPage ? <LoadingButton className="btn btn-default" href={currentUrl + "?after=" + encodeURIComponent(this.props.pageInfo.endCursor)} onClick={this.nextPage}><FormattedMessage defaultMessage="Load more…" id="msg.loadMore" /></LoadingButton> : null}
            </div>
        );
    }
}
export default injectIntl(GridList);
